/**
 * Regular Gallery — scoped Shopify video playback for existing image blocks.
 * Loaded only when an Image block has a video. Does not use Gallery by Notch or Image with text classes.
 * Autoplay players mount only when approaching the viewport.
 */
(function () {
  if (customElements.get('gallery-video')) return;

  var INIT_MARGIN = '400px 0px 400px 0px';
  var PLAY_THRESHOLD = 0.25;

  class GalleryVideo extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;

      this.playback = this.dataset.playback || 'play_button';
      this.allowMobileAutoplay = this.dataset.allowMobileAutoplay === 'true';
      this.loopEnabled = this.dataset.loop !== 'false';
      this.showControls = this.dataset.controls === 'true';
      this.adaptRatio = this.dataset.adapt === 'true';
      this.player = null;
      this.poster = this.querySelector('[data-gallery-poster]');
      this.card = this.closest('.gallery__card');
      this.playControl = this.card ? this.card.querySelector('[data-gallery-play]') : this.querySelector('[data-gallery-play]');
      this.template = this.querySelector('template');
      this.stage = this.querySelector('.gallery__video-stage');
      this.mediaBox = this.closest('.media');
      this.userPaused = false;
      this.pausingFromObserver = false;
      this.isIntersecting = false;
      this.nearViewport = false;
      this.mediaListenersAttached = false;
      this.playBound = false;
      this.reducedQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.mobileQuery = window.matchMedia('(max-width: 749px)');

      this.onReducedChange = this.handleReducedChange.bind(this);
      this.onMobileChange = this.handleMobileChange.bind(this);
      this.onInitIntersect = this.handleInitIntersect.bind(this);
      this.onPlayIntersect = this.handlePlayIntersect.bind(this);
      this.onPlay = this.handlePlay.bind(this);
      this.onPause = this.handlePause.bind(this);
      this.onCanPlay = this.handleCanPlay.bind(this);
      this.onMediaError = this.handleMediaError.bind(this);
      this.onLoadedMeta = this.handleLoadedMeta.bind(this);
      this.onPlayClick = this.handlePlayClick.bind(this);
      this.onSectionUnload = this.handleSectionUnload.bind(this);

      this.bindMediaQuery(this.reducedQuery, this.onReducedChange);
      this.bindMediaQuery(this.mobileQuery, this.onMobileChange);
      document.addEventListener('shopify:section:unload', this.onSectionUnload);
      if (this.prefersReducedMotion()) this.classList.add('is-reduced-motion');

      this.bindPlayControl();
      if (this.shouldAutoplay()) this.hidePlayControl();
      else if (this.playback === 'autoplay') this.showPlayControl();
      this.observeInit();
      this.observePlay();
    }

    disconnectedCallback() {
      this.cleanup();
    }

    bindMediaQuery(query, handler) {
      if (!query) return;
      if (typeof query.addEventListener === 'function') query.addEventListener('change', handler);
      else if (typeof query.addListener === 'function') query.addListener(handler);
    }

    unbindMediaQuery(query, handler) {
      if (!query) return;
      if (typeof query.removeEventListener === 'function') query.removeEventListener('change', handler);
      else if (typeof query.removeListener === 'function') query.removeListener(handler);
    }

    prefersReducedMotion() {
      return this.reducedQuery && this.reducedQuery.matches;
    }

    isMobile() {
      return this.mobileQuery && this.mobileQuery.matches;
    }

    shouldAutoplay() {
      if (this.playback !== 'autoplay') return false;
      if (this.prefersReducedMotion()) return false;
      if (!this.allowMobileAutoplay && this.isMobile()) return false;
      return true;
    }

    bindPlayControl() {
      if (!this.playControl || this.playBound) return;
      this.playControl.addEventListener('click', this.onPlayClick);
      this.playBound = true;
    }

    observeInit() {
      if (this.initObserver || typeof IntersectionObserver === 'undefined') {
        if (!this.initObserver && this.shouldAutoplay()) this.mountForAutoplay();
        return;
      }
      this.initObserver = new IntersectionObserver(this.onInitIntersect, {
        rootMargin: INIT_MARGIN,
        threshold: 0
      });
      this.initObserver.observe(this);
    }

    observePlay() {
      if (this.playObserver || typeof IntersectionObserver === 'undefined') return;
      this.playObserver = new IntersectionObserver(this.onPlayIntersect, {
        threshold: PLAY_THRESHOLD,
        rootMargin: '0px 0px 40px 0px'
      });
      this.playObserver.observe(this);
    }

    disconnectObservers() {
      if (this.initObserver) {
        this.initObserver.disconnect();
        this.initObserver = null;
      }
      if (this.playObserver) {
        this.playObserver.disconnect();
        this.playObserver = null;
      }
    }

    handleInitIntersect(entries) {
      entries.forEach((entry) => {
        this.nearViewport = entry.isIntersecting;
        if (entry.isIntersecting && this.shouldAutoplay()) this.mountForAutoplay();
      });
    }

    handlePlayIntersect(entries) {
      entries.forEach((entry) => {
        this.isIntersecting = entry.isIntersecting;
        if (entry.isIntersecting) {
          if (this.userPaused) return;
          if (this.shouldAutoplay()) {
            this.mountForAutoplay();
            this.tryPlay();
            return;
          }
          if (this.activated && this.player) this.tryPlay();
        } else if (this.player && !this.player.paused) {
          this.pausingFromObserver = true;
          this.player.pause();
          this.pausingFromObserver = false;
        }
      });
    }

    mountForAutoplay() {
      if (!this.shouldAutoplay()) return;
      this.mountPlayer({ muted: true, autoplayIntent: true });
      if (this.isIntersecting) this.tryPlay();
    }

    mountPlayer(options) {
      options = options || {};
      if (this.player && this.player.isConnected) {
        this.applyPlayerFlags(options);
        return;
      }
      if (!this.template || !this.stage) return;
      var node = this.template.content.querySelector('video');
      if (!node) return;
      this.player = node.cloneNode(true);
      this.player.classList.add('gallery__video-player', 'no-js-hidden');
      this.player.removeAttribute('autoplay');
      this.applyPlayerFlags(options);
      this.stage.appendChild(this.player);
      this.attachMediaListeners();
    }

    applyPlayerFlags(options) {
      if (!this.player) return;
      this.player.loop = this.loopEnabled;
      this.player.playsInline = true;
      this.player.setAttribute('playsinline', '');
      this.player.setAttribute('webkit-playsinline', '');
      if (this.showControls) {
        this.player.setAttribute('controls', '');
        this.player.controls = true;
      } else {
        this.player.removeAttribute('controls');
        this.player.controls = false;
      }
      if (options.muted || this.shouldAutoplay()) {
        this.player.muted = true;
        this.player.setAttribute('muted', '');
      }
      if (options.autoplayIntent) this.player.preload = 'metadata';
    }

    attachMediaListeners() {
      if (!this.player || this.mediaListenersAttached) return;
      this.player.addEventListener('play', this.onPlay);
      this.player.addEventListener('pause', this.onPause);
      this.player.addEventListener('canplay', this.onCanPlay);
      this.player.addEventListener('error', this.onMediaError);
      this.player.addEventListener('loadedmetadata', this.onLoadedMeta);
      this.mediaListenersAttached = true;
    }

    handlePlayClick(event) {
      event.preventDefault();
      event.stopPropagation();
      this.activated = true;
      this.userPaused = false;
      this.classList.remove('is-reduced-motion');
      this.hidePoster();
      this.mountPlayer({ muted: false, autoplayIntent: false });
      if (this.player) {
        this.player.muted = false;
        this.player.removeAttribute('muted');
      }
      this.tryPlay();
    }

    hidePlayControl() {
      if (this.playControl) this.playControl.hidden = true;
    }

    showPlayControl() {
      if (this.playControl) this.playControl.hidden = false;
    }

    showPoster() {
      if (this.poster) this.poster.hidden = false;
      this.showPlayControl();
      this.classList.add('is-poster');
    }

    hidePoster() {
      if (this.poster) this.poster.hidden = true;
      this.hidePlayControl();
      this.classList.remove('is-poster');
    }

    handleReducedChange() {
      if (this.activated && this.player && !this.player.paused) return;
      if (this.shouldAutoplay()) {
        this.classList.remove('is-reduced-motion');
        this.hidePlayControl();
        if (this.nearViewport) this.mountForAutoplay();
        return;
      }
      this.pauseForFallback();
    }

    handleMobileChange() {
      if (this.playback !== 'autoplay') return;
      if (this.shouldAutoplay()) {
        this.hidePlayControl();
        if (this.nearViewport) this.mountForAutoplay();
        return;
      }
      if (this.activated) return;
      this.pauseForFallback();
    }

    pauseForFallback() {
      if (this.player) this.player.pause();
      this.classList.add('is-reduced-motion');
      this.showPoster();
    }

    handleLoadedMeta() {
      if (!this.adaptRatio || !this.player) return;
      var width = this.player.videoWidth;
      var height = this.player.videoHeight;
      if (!width || !height) return;
      var ratio = width / height;
      if (ratio < 0.2 || ratio > 5) return;
      if (this.mediaBox) this.mediaBox.style.setProperty('--gallery-media-aspect', String(ratio));
      this.style.setProperty('--gallery-video-aspect', String(ratio));
    }

    handleCanPlay() {
      if (this.userPaused) return;
      if (this.shouldAutoplay()) {
        if (this.playObserver && !this.isIntersecting) return;
        this.tryPlay();
        return;
      }
      if (this.playControl && !this.playControl.hidden) return;
      if (!this.activated) return;
      if (this.playObserver && !this.isIntersecting) return;
      this.tryPlay();
    }

    handleMediaError() {
      this.pauseForFallback();
    }

    tryPlay() {
      if (!this.player) return;
      var playAttempt = this.player.play();
      if (playAttempt && typeof playAttempt.catch === 'function') {
        playAttempt.catch((error) => {
          if (!this.isConnected || this.userPaused) return;
          if (error && error.name === 'AbortError') return;
          this.pauseForFallback();
        });
      }
    }

    handlePlay() {
      this.userPaused = false;
      this.classList.add('is-playing');
      if (this.card) this.card.classList.add('gallery__card--playing');
      this.hidePoster();
    }

    handlePause() {
      this.classList.remove('is-playing');
      if (this.card) this.card.classList.remove('gallery__card--playing');
      if (this.pausingFromObserver) return;
      if (this.isIntersecting) this.userPaused = true;
      if (this.userPaused && !this.showControls && this.playback === 'play_button') this.showPoster();
    }

    handleSectionUnload(event) {
      var section = this.closest('.shopify-section');
      if (section && event.target === section) this.cleanup();
    }

    cleanup() {
      this.disconnectObservers();
      if (this.card) this.card.classList.remove('gallery__card--playing');
      if (this.player) {
        this.player.removeEventListener('play', this.onPlay);
        this.player.removeEventListener('pause', this.onPause);
        this.player.removeEventListener('canplay', this.onCanPlay);
        this.player.removeEventListener('error', this.onMediaError);
        this.player.removeEventListener('loadedmetadata', this.onLoadedMeta);
        this.player.pause();
        this.player.removeAttribute('src');
        this.player.load();
      }
      if (this.playControl && this.playBound) {
        this.playControl.removeEventListener('click', this.onPlayClick);
        this.playBound = false;
      }
      this.unbindMediaQuery(this.reducedQuery, this.onReducedChange);
      this.unbindMediaQuery(this.mobileQuery, this.onMobileChange);
      document.removeEventListener('shopify:section:unload', this.onSectionUnload);
      this.mediaListenersAttached = false;
      this._bound = false;
    }
  }

  customElements.define('gallery-video', GalleryVideo);

  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('gallery-video').forEach((el) => {
      if (typeof el.connectedCallback === 'function' && !el._bound) el.connectedCallback();
    });
  });
})();
